import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  TrendingUp, Shield, Heart, Target, PiggyBank, Briefcase,
  Award, Clock, MapPin
} from 'lucide-react';
import { companyInfo, stats, services, testimonials } from '../data/mock';

const Home = () => {
  const handleWhatsApp = () => {
    window.open(companyInfo.whatsappLink, '_blank');
  };

  const getIcon = (iconName) => {
    const icons = {
      TrendingUp, Shield, Heart, Target, PiggyBank, Briefcase
    };
    const Icon = icons[iconName] || TrendingUp;
    return <Icon className="w-8 h-8" />;
  };

  return (
    <div className="min-h-screen bg-[#0f1419]">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f1419] via-[#1a1f2e] to-[#0f1419] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzI2MzIzOCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-5"></div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                Building Wealth Through{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">
                  Disciplined Investing
                </span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Expert financial advisory for corporate professionals. Strategic wealth optimization through mutual funds, insurance, and goal-based planning.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-8 py-6 text-lg shadow-lg shadow-[#F59E0B]/30"
                >
                  Start Your Journey
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 bg-transparent px-8 py-6 text-lg"
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/20 to-[#FBBF24]/20 blur-3xl rounded-full"></div>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBwbGFubmluZ3xlbnwwfHx8fDE3NzAyMzA1ODd8MA&ixlib=rb-4.1.0&q=85"
                alt="Financial Planning"
                className="rounded-2xl shadow-2xl w-full h-auto relative z-10 border border-gray-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-[#1a1f2e] to-[#0f1419] border-y border-gray-800">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] mb-2">
                  {stat.value}
                </div>
                <div className="text-lg text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services" className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive financial solutions tailored for corporate professionals
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-[#1a1f2e] border-gray-800 hover:border-[#F59E0B] transition-all hover:shadow-lg hover:shadow-[#F59E0B]/10 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#F59E0B]/10 rounded-xl flex items-center justify-center text-[#F59E0B] mb-6 group-hover:bg-[#F59E0B]/20 transition-colors">
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4 text-white">
                    {service.title}
                  </h3>
                  <p className="text-[17px] text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzcwMjQ3MzM5fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional Advisory"
                className="rounded-2xl shadow-xl w-full h-auto border border-gray-800"
              />
            </div>
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">WealthOptimizers</span>?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                We're not just financial advisors – we're your partners in building lasting wealth.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Award, text: "AMFI Registered & Compliant" },
                  { icon: Clock, text: "12+ Years Industry Experience" },
                  { icon: MapPin, text: "Pan-India Service Network" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 bg-[#1a1f2e]/50 rounded-lg border border-gray-800 hover:border-[#F59E0B]/50 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] rounded-lg flex items-center justify-center text-black flex-shrink-0">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <span className="text-lg text-gray-300 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="mt-8 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-8 py-6 text-lg"
              >
                Connect With Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 bg-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-400">
              Real experiences from real investors
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-[#1a1f2e] border-gray-800">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-[#F59E0B] fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-[17px] text-gray-300 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-lg text-white">{testimonial.name}</div>
                    <div className="text-[17px] text-[#F59E0B]">{testimonial.designation}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1f2e] to-[#0f1419]">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="bg-gradient-to-r from-[#F59E0B]/10 to-[#FBBF24]/10 border-2 border-[#F59E0B]/30">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Start Your Wealth Journey?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Let's discuss your financial goals and create a personalized investment strategy
              </p>
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold px-10 py-6 text-lg shadow-lg shadow-[#F59E0B]/30"
              >
                Talk to an Expert Now
              </Button>
              <p className="text-sm text-gray-500 mt-6">
                {companyInfo.disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;